import { createContext, useContext, useMemo } from 'react'

/**
 * Global project module permission guard.
 *
 * Permissions come from the project detail API response as
 * `supportProjectPermission`, e.g.:
 * {
 *   support:     { isRead, isEdit, isDelete },
 *   development: { isRead, isEdit, isDelete },
 *   storymap:    { isRead, isEdit, isDelete },
 *   test:        { isRead, isEdit, isDelete },
 * }
 *
 * Rules:
 * - Super admins bypass every restriction.
 * - Modules not listed in ENFORCED_MODULES are not restricted yet
 *   (add 'development' here when that tab must be guarded too).
 * - Users without a permission payload (e.g. team members) keep full access.
 */

export const PROJECT_MODULE_SUPPORT = 'support'
export const PROJECT_MODULE_DEVELOPMENT = 'development'

const ENFORCED_MODULES = new Set([PROJECT_MODULE_SUPPORT])

export const FULL_MODULE_ACCESS = Object.freeze({
    canView: true,
    canEdit: true,
    canDelete: true,
})

export const ProjectPermissionsContext = createContext({
    isSuperAdmin: false,
    modules: null,
})

export const taskListKindToModule = (taskListKind) =>
    taskListKind === 'main' ? PROJECT_MODULE_DEVELOPMENT : PROJECT_MODULE_SUPPORT

export const resolveModulePermission = (moduleKey, { isSuperAdmin, modules }) => {
    if (isSuperAdmin) return FULL_MODULE_ACCESS
    if (!ENFORCED_MODULES.has(moduleKey)) return FULL_MODULE_ACCESS

    const permission = modules?.[moduleKey]
    if (!permission) return FULL_MODULE_ACCESS

    const canView = Boolean(permission.isRead)
    return {
        canView,
        canEdit: canView && Boolean(permission.isEdit),
        canDelete: canView && Boolean(permission.isDelete),
    }
}

export const useProjectModulePermission = (moduleKey) => {
    const context = useContext(ProjectPermissionsContext)
    return useMemo(
        () => resolveModulePermission(moduleKey, context),
        [moduleKey, context]
    )
}

export const useTaskListKindPermission = (taskListKind) =>
    useProjectModulePermission(taskListKindToModule(taskListKind))
